import { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import i18n from '@/utils/i18n';
import { useIsMobile } from '@/hooks/use-mobile';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  /**
   * Default [10, 20, 50, 100]
   */
  availablePageSizes?: number[];
  showSelectedRowCount?: boolean;
}

export function DataTablePagination<TData>({
  table,
  availablePageSizes = [10, 20, 50, 100],
  showSelectedRowCount = true,
}: DataTablePaginationProps<TData>) {
  const isMobile = useIsMobile();
  const rowCount = table.getRowCount();
  const shouldHidePagination = rowCount <= Math.min(...availablePageSizes);
  const availablePageSizesAccordingToTable = shouldHidePagination
    ? []
    : availablePageSizes
        .toSorted((a, b) => a - b)
        .slice(
          0,
          availablePageSizes.findIndex((pageSize) => rowCount <= pageSize) + 1,
        );

  const currentPage = table.getState().pagination.pageIndex + 1;
  const pageCount = table.getPageCount();
  const paginationText = isMobile
    ? i18n.t('Common:paginationPageShort', {
        current: currentPage,
        total: pageCount,
      })
    : i18n.t('Common:paginationPageLong', {
        current: currentPage,
        total: pageCount,
      });

  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:px-2">
      {showSelectedRowCount && (
        <div className="tw:text-muted-foreground tw:flex-1 tw:text-sm tw:tabular-nums">
          {i18n.t('Common:rowsSelectedSummary', {
            selected: table.getFilteredSelectedRowModel().rows.length,
            total: rowCount,
          })}
        </div>
      )}
      {!showSelectedRowCount && (
        <div className="tw:flex-1 tw:hidden tw:sm:flex" />
      )}
      {!shouldHidePagination && (
        <div className="tw:flex tw:items-center tw:space-x-6 tw:lg:space-x-8">
          <div className="tw:flex tw:items-center tw:space-x-2">
            <p className="tw:text-sm tw:font-medium">
              {i18n
                .t('Common:rowsPerPage')
                .split(' ')
                .slice(0, isMobile ? 1 : undefined)
                .join(' ')}
            </p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="tw:h-8 tw:w-[70px]">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {availablePageSizesAccordingToTable.map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tw:flex tw:tabular-nums tw:items-center tw:justify-center tw:text-sm tw:font-medium">
            {paginationText}
          </div>
          <div className="tw:flex tw:items-center tw:space-x-2">
            {table.getPageCount() > 3 && (
              <Button
                variant="outline"
                size="icon"
                className="tw:size-8 tw:lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="tw:sr-only">Go to first page</span>
                <ChevronsLeft />
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              className="tw:size-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="tw:sr-only">Go to previous page</span>
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="tw:size-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="tw:sr-only">Go to next page</span>
              <ChevronRight />
            </Button>
            {table.getPageCount() > 3 && (
              <Button
                variant="outline"
                size="icon"
                className="tw:size-8 tw:lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="tw:sr-only">Go to last page</span>
                <ChevronsRight />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
